import { toDateOnly } from "utils/Common/Date";
import {
  ActionStatus,
  ApiActionStatus,
  ApiMoneyAmount,
  ApiBudgetLineItem,
  ApiComparisonProject,
  ChecklistDocumentItem,
  DocStatus,
  PreconFormValues,
  PreconDraftPayload,
} from "../types/precon.types";
import { DEFAULT_YES_NO_ITEMS, BUDGET_TOTAL_ROW } from "./normalize";

// ── Delivery method constants ─────────────────────────────────────────────────

export const DELIVERY_METHOD_OPTIONS = {
  EMAIL_PLATFORM: "email_platform",
  PLATFORM: "platform",
  EMAIL: "email",
} as const;

const DELIVERY_METHOD_TO_ARRAY: Record<string, string[]> = {
  [DELIVERY_METHOD_OPTIONS.EMAIL_PLATFORM]: ["email", "push"],
  [DELIVERY_METHOD_OPTIONS.PLATFORM]: ["push"],
  [DELIVERY_METHOD_OPTIONS.EMAIL]: ["email"],
};

const DELIVERY_ARRAY_TO_METHOD = (arr: string[] = []): string => {
  const has = (v: string) => arr?.includes(v);
  if (has("email") && has("push"))
    return DELIVERY_METHOD_OPTIONS.EMAIL_PLATFORM;
  if (has("push")) return DELIVERY_METHOD_OPTIONS.PLATFORM;
  return DELIVERY_METHOD_OPTIONS.EMAIL;
};

// ── Conversion helpers ────────────────────────────────────────────────────────

/** "$8,398,472" | "8398472" | 8398472 → 8398472 */
const parseMoneyStr = (str: string | number | null | undefined): number => {
  if (str == null) return 0;
  if (typeof str === "number") return Number.isFinite(str) ? str : 0;
  return parseFloat(str.replace(/[$,\s]/g, "")) || 0;
};

/** "$678" → { amount: 678, currencyCode: "USD" }  |  "" → undefined */
const toMoneyObj = (
  str: string | number | null | undefined,
): ApiMoneyAmount | undefined => {
  const n = parseMoneyStr(str);
  return n > 0
    ? ({ amount: n, currencyCode: "USD" } as ApiMoneyAmount)
    : undefined;
};

/** "1200 SF" | "28,011" | "121.06" → number | undefined */
const parseNumStr = (str: string): number | undefined => {
  const n = parseFloat(str.replace(/[^0-9.]/g, ""));
  return isNaN(n) ? undefined : (n as number);
};

/** { amount: 678 } → "$678"  |  undefined → "" */
const fmtMoneyObj = (obj: ApiMoneyAmount | undefined): string =>
  obj?.amount != null && obj.amount > 0
    ? `$${obj.amount.toLocaleString()}`
    : "";

/** 1200 → "1200 SF"  |  0 | undefined → "" */
const fmtSf = (n: number | undefined): string =>
  n != null && n > 0 ? `${n} SF` : "";

/** 1200 → "1200"  |  0 | undefined → "" */
const fmtNum = (n: number | undefined): string =>
  n != null && n > 0 ? String(n) : "";

/** "01/15/2024" | "2024-01-15" → "2024-01-15"  |  unparseable | "" → undefined */
const toIsoDate = (str: string): string | undefined =>
  str ? toDateOnly(str) : undefined;

/** "Pending" → "pending" | "Complete" → "complete" | "N/A" → "na" */
const toApiStatus = (s: ActionStatus): ApiActionStatus =>
  s === "Complete" ? "complete" : s === "N/A" ? "na" : "pending";

/** "pending" → "Pending" | "complete" → "Complete" | "na" → "N/A" (backward-compat: capitalised passthrough) */
const fromApiStatus = (s: string | undefined): ActionStatus => {
  if (s === "complete" || s === "Complete") return "Complete";
  if (s === "na" || s === "N/A") return "N/A";
  return "Pending";
};

// ── Build payload (form values → API) ────────────────────────────────────────

export function buildPreconPayload(
  values: PreconFormValues,
): PreconDraftPayload {
  const allDocs = [...values.docsLeft, ...values.docsRight];

  return {
    projectSetup: {
      servicesRequested: values.servicesRequested,
      reportDate: values.reportDate,
      reportInspectionDate: values.reviewDate || undefined,
      reportInvoiceNumber: values.invoiceNumber || undefined,
      reportInvoiceAmount: toMoneyObj(values.invoiceAmount) ?? undefined,
      reportPreparedByName: values.preparedBy || undefined,
      checklistItems: allDocs
        .filter((d) => d.id)
        .map(
          (doc): ChecklistDocumentItem => ({
            type: "document",
            key: doc.id,
            value: doc.status === "done" ? (doc.rawValue ?? null) : null,
          }),
        ),
    },
    certification: {
      certificationOpening: values.certificationOpening,
      certificationStandards: values.certificationStandards || undefined,
      certificationFindings: values.certificationFindings || undefined,
      certificationSigner: values.certificationPreparedBy || undefined,
      certificationSignerCompanyName:
        values.certificationOrganization || undefined,
      certificationPhone: values.certificationPhone,
      certificationEmail: values.certificationEmail,
    },
    executiveSummary: {
      execSummaryDescription: values.primaryDescriptionPoints || undefined,
      totalSf: parseNumStr(values.totalSf) as number | undefined,
      totalUnitsPhase: parseNumStr(values.totalUnitsPhase1) as
        | number
        | undefined,
      totalUnitsFull: parseNumStr(values.totalUnitsFull) as number | undefined,
      execSummaryScopeNotes: values.additionalDescription || undefined,
      totalBudget: toMoneyObj(values.totalBudget) as ApiMoneyAmount | undefined,
      hardCostBudget: toMoneyObj(values.hardCostBudget) as
        | ApiMoneyAmount
        | undefined,
      costPerSf: parseNumStr(values.costPerSf) as number | undefined,
      mobilizationDate: toIsoDate(values.mobilizationDate),
      estCompletionDate: toIsoDate(values.estCompletion),
    },
    contractDelivery: {
      contractType: values.contractType,
      contractorFee: toMoneyObj(values.contractorFee) as
        | ApiMoneyAmount
        | undefined,
      contingencyAmount: toMoneyObj(values.contingencyDisplay) as
        | ApiMoneyAmount
        | undefined,
      retainage: parseNumStr(values.retainage),
      yesNoItems: values.yesNoItems,
      contractKeyFindings: values.contractNarrative || undefined,
    },
    allowancesContingencies: {
      allowancesNarrative: values.allowancesNarrative,
      gcContingencyAmount: toMoneyObj(values.gcContingencyAmount) as
        | ApiMoneyAmount
        | undefined,
      gcContingencyPercentage: parseNumStr(values.contingencyPct),
      contingencyTypicalRange: values.industryRange || undefined,
      contingencyNarrative: values.contingencyNarrative,
      clarificationsNarrative: values.clarificationsNarrative,
    },
    budgetAnalysis: {
      budgetAnalysisNarrative: values.budgetNarrative || undefined,
      budgetAnalysisConclusion: values.budgetConclusion || undefined,
      budgetLineItems: values.budgetRows
        .filter((row) => !row.isTotal)
        .map(
          (row, i): ApiBudgetLineItem => ({
            id: row.id,
            tradeName: row.trade,
            scheduledValue: toMoneyObj(row.value),
            projectSf: parseNumStr(row.projectSf),
            costPerSf: parseNumStr(row.projectPsf),
            industryLow: parseNumStr(row.industryLow),
            industryHigh: parseNumStr(row.industryHigh),
            notes: row.notes || undefined,
            displayOrder: i,
          }),
        ),
      comparisonProjects: values.comparisonRows
        .filter((row) => !row.isThis)
        .map(
          (row, i): ApiComparisonProject => ({
            id: row.id,
            name: row.project,
            units: row.units || undefined,
            budget: toMoneyObj(row.budget),
            costLessSitework: toMoneyObj(row.costLessSitework),
            costPerUnit: toMoneyObj(row.costPerUnit),
            totalSf: parseNumStr(row.totalSf),
            costPerSf: toMoneyObj(row.costPerSf),
            displayOrder: i,
          }),
        ),
      borrowerEquityAmount:
        toMoneyObj(values.borrowerEquityAmount) ?? undefined,
      borrowerEquityDescription: values.borrowerEquityDescription || undefined,
    },
    scheduleClosingActions: {
      scheduleAnalysis: {
        masterScheduleProvided:
          values.masterScheduleProvided === "Yes — attached",
        typicalDuration: values.typicalDuration,
        statedDuration: values.statedDuration,
      },
      scheduleNarrative: values.scheduleNarrative,
      closingComments: values.closingNarrative || undefined,
      actionItems: values.actionItems.map((item, i) => ({
        party: item.party,
        actionDescription: item.action,
        status: toApiStatus(item.status),
        displayOrder: i,
      })),
      documentsReviewed: values.documentsReviewed,
      preconRequirements: values.preconRequirements,
      drawRequirements: values.drawRequirements,
      closeoutRequirements: values.closeoutRequirements,
    },
    deliverySettings: {
      deliverTo: values.deliverTo,
      deliverCc: values.deliveryCC.length ? values.deliveryCC : undefined,
      deliveryMethod: DELIVERY_METHOD_TO_ARRAY[values.deliveryMethod] ?? [
        "email",
      ],
      lockAfterPublish: values.lockAfterPublish === "yes",
    },
    bankDrawRequirement: {
      preConstructionRequirement: values.preconRequirements || undefined,
      constructionDrawRequirment: values.drawRequirements || undefined,
      constructionClosingRequirment: values.closeoutRequirements || undefined,
    },
    documentsReviewedList: values.documentsReviewed || undefined,
  };
}

// ── Normalize draft (API → form values) ──────────────────────────────────────

const fmtPerson = (
  p?: { firstName?: string; lastName?: string; companyName?: string } | null,
): string => {
  if (!p) return "";
  const fullName = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim();
  return [p.companyName, fullName].filter(Boolean).join(" | ");
};

export function normalizeDraft(draft: PreconDraftPayload): PreconFormValues {
  const proj = draft.project;
  const ps = draft.projectSetup;
  const cert = draft.certification;
  const exec = draft.executiveSummary;
  const contract = draft.contractDelivery;
  const allow = draft.allowancesContingencies;
  const budget = draft.budgetAnalysis;
  const sched = draft.scheduleClosingActions;
  const delivery = draft.deliverySettings;
  const bankDraw = draft.bankDrawRequirement;

  const rawSf = exec?.totalSf;

  return {
    // Step 1 — project-level fields from `data.project`, form fields from `data.projectSetup`
    bank: proj?.lender?.companyName ?? "",
    lendingOfficer: proj?.lendingOfficer
      ? `${proj.lendingOfficer.firstName ?? ""} ${proj.lendingOfficer.lastName ?? ""}`.trim()
      : "",
    projectName: proj?.name ?? "",
    projectDescription: proj?.description ?? "",
    projectType: (proj?.projectType ?? proj?.type ?? "").toLowerCase(),
    ownerName: fmtPerson(proj?.owner),
    gcName: fmtPerson(proj?.gc),
    architectName: fmtPerson(proj?.architect),
    servicesRequested:
      ps?.servicesRequested || "Scope of Work, Plan & Cost Review",
    reportDate: toIsoDate(ps?.reportDate ?? "") ?? "",
    invoiceNumber: ps?.reportInvoiceNumber ?? "",
    invoiceAmount:
      typeof ps?.reportInvoiceAmount === "object" &&
      ps.reportInvoiceAmount != null
        ? String((ps.reportInvoiceAmount as ApiMoneyAmount).amount ?? "")
        : ((ps?.reportInvoiceAmount as string | undefined) ?? ""),
    preparedBy: ps?.reportPreparedByName ?? "",
    projectAddress: proj?.address ?? "",
    reviewDate: toIsoDate(ps?.reportInspectionDate ?? "") ?? "",
    docsLeft: (() => {
      const raw = (ps?.checklistItems ?? []) as unknown as Array<
        Record<string, unknown>
      >;
      const mid = Math.ceil(raw.length / 2);
      const fmtDocLabel = (k: string) =>
        k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return raw.slice(0, mid).map((d) => {
        const val = d.value as
          | { key?: string; fileName?: string }
          | null
          | undefined;
        const key = (d.key ?? d.id ?? "") as string;
        return {
          id: key,
          label: (d.label as string | undefined) ?? fmtDocLabel(key),
          status: (val != null ? "done" : "na") as DocStatus,
          rawValue: val?.key
            ? {
                key: val.key as string,
                fileName: (val.fileName ?? "") as string,
              }
            : null,
        };
      });
    })(),
    docsRight: (() => {
      const raw = (ps?.checklistItems ?? []) as unknown as Array<
        Record<string, unknown>
      >;
      const mid = Math.ceil(raw.length / 2);
      const fmtDocLabel = (k: string) =>
        k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return raw.slice(mid).map((d) => {
        const val = d.value as
          | { key?: string; fileName?: string }
          | null
          | undefined;
        const key = (d.key ?? d.id ?? "") as string;
        return {
          id: key,
          label: (d.label as string | undefined) ?? fmtDocLabel(key),
          status: (val != null ? "done" : "na") as DocStatus,
          rawValue: val?.key
            ? {
                key: val.key as string,
                fileName: (val.fileName ?? "") as string,
              }
            : null,
        };
      });
    })(),

    // Step 2
    certificationOpening: cert?.certificationOpening ?? "",
    certificationStandards: cert?.certificationStandards ?? "",
    certificationFindings: cert?.certificationFindings ?? "",
    certificationPreparedBy: cert?.certificationSigner ?? "",
    certificationOrganization: cert?.certificationSignerCompanyName ?? "",
    certificationPhone: cert?.certificationPhone ?? "",
    certificationEmail: cert?.certificationEmail ?? "",

    // Step 3
    primaryDescriptionPoints: exec?.execSummaryDescription ?? "",
    totalSf: fmtSf(rawSf != null ? parseFloat(String(rawSf)) : undefined),
    totalUnitsPhase1: fmtNum(exec?.totalUnitsPhase ?? undefined),
    totalUnitsFull: fmtNum(exec?.totalUnitsFull ?? undefined),
    additionalDescription: exec?.execSummaryScopeNotes ?? "",
    totalBudget: fmtMoneyObj(exec?.totalBudget ?? undefined),
    hardCostBudget: fmtMoneyObj(exec?.hardCostBudget ?? undefined),
    costPerSf: fmtNum(exec?.costPerSf ?? undefined),
    mobilizationDate: toIsoDate(exec?.mobilizationDate ?? "") ?? "",
    estCompletion: toIsoDate(exec?.estCompletionDate ?? "") ?? "",

    // Step 4
    contractType: contract?.contractType ?? "",
    contractorFee: fmtMoneyObj(contract?.contractorFee ?? undefined),
    contingencyDisplay: fmtMoneyObj(contract?.contingencyAmount ?? undefined),
    retainage: contract?.retainage != null ? String(contract.retainage) : "",
    yesNoItems: (() => {
      const saved = contract?.yesNoItems ?? [];
      if (!saved.length) return DEFAULT_YES_NO_ITEMS;
      const valMap = new Map(saved.map((it) => [it.id, it.value]));
      return DEFAULT_YES_NO_ITEMS.map((item) => ({
        ...item,
        value: valMap.has(item.id) ? valMap.get(item.id)! : item.value,
      }));
    })(),
    contractNarrative: contract?.contractKeyFindings ?? "",

    // Step 5
    allowancesNarrative: allow?.allowancesNarrative ?? "",
    gcContingencyAmount: fmtMoneyObj(allow?.gcContingencyAmount ?? undefined),
    contingencyPct:
      allow?.gcContingencyPercentage != null
        ? String(allow.gcContingencyPercentage)
        : "",
    industryRange: allow?.contingencyTypicalRange ?? "5.0% – 10.0%",
    contingencyNarrative: allow?.contingencyNarrative ?? "",
    clarificationsNarrative: allow?.clarificationsNarrative ?? "",

    // Step 6
    budgetRows: [
      ...(budget?.budgetLineItems?.map((item) => ({
        id: item.id ?? `budget-${Math.random().toString(36).slice(2)}`,
        trade: item.tradeName ?? "",
        value: fmtMoneyObj(item.scheduledValue ?? undefined),
        projectSf: item.projectSf != null ? String(item.projectSf) : "",
        projectPsf: item.costPerSf != null ? String(item.costPerSf) : "",
        industryLow: item.industryLow != null ? String(item.industryLow) : "",
        industryHigh:
          item.industryHigh != null ? String(item.industryHigh) : "",
        notes: item.notes ?? "",
      })) ?? []),
      BUDGET_TOTAL_ROW,
    ],
    comparisonRows:
      budget?.comparisonProjects?.map((item) => ({
        id: item.id ?? `comp-${Math.random().toString(36).slice(2)}`,
        project: item.name ?? "",
        units: item.units != null ? String(item.units) : "",
        budget: fmtMoneyObj(item.budget),
        costLessSitework: fmtMoneyObj(item.costLessSitework),
        costPerUnit: fmtMoneyObj(item.costPerUnit),
        totalSf: item.totalSf != null ? String(item.totalSf) : "",
        costPerSf: fmtMoneyObj(item.costPerSf),
        isThis: false,
      })) ?? [],
    budgetNarrative: budget?.budgetAnalysisNarrative ?? "",
    budgetConclusion: budget?.budgetAnalysisConclusion ?? "",
    borrowerEquityRequired: false,
    borrowerEquityAmount: fmtMoneyObj(
      budget?.borrowerEquityAmount ?? undefined,
    ),
    borrowerEquityDescription: budget?.borrowerEquityDescription ?? "",

    // Step 7
    masterScheduleProvided: sched?.scheduleAnalysis?.masterScheduleProvided
      ? "Yes — attached"
      : "No — not provided",
    typicalDuration:
      sched?.scheduleAnalysis?.typicalDuration ??
      sched?.typicalDuration ??
      "12–16 months",
    statedDuration:
      sched?.scheduleAnalysis?.statedDuration ?? sched?.statedDuration ?? "",
    scheduleNarrative: sched?.scheduleNarrative ?? "",
    closingNarrative: sched?.closingComments ?? "",
    actionItems:
      sched?.actionItems?.map((item, i) => ({
        id: item.id ?? `action-${item.displayOrder ?? i}`,
        party: item.party ?? "",
        action: item.actionDescription ?? item.action ?? "",
        status: fromApiStatus(item.status as string | undefined),
      })) ?? [],
    documentsReviewed:
      draft.documentsReviewedList ?? sched?.documentsReviewed ?? "",
    preconRequirements:
      bankDraw?.preConstructionRequirement ?? sched?.preconRequirements ?? "",
    drawRequirements:
      bankDraw?.constructionDrawRequirment ?? sched?.drawRequirements ?? "",
    closeoutRequirements:
      bankDraw?.constructionClosingRequirment ??
      sched?.closeoutRequirements ??
      "",

    // Step 8
    deliverTo: delivery?.deliverTo ?? "",
    deliveryCC: Array.isArray(delivery?.deliverCc)
      ? delivery.deliverCc
      : delivery?.deliverCc
        ? [delivery.deliverCc]
        : [],
    deliveryMethod: DELIVERY_ARRAY_TO_METHOD(
      delivery?.deliveryMethod as string[] | undefined,
    ),
    lockAfterPublish: delivery?.lockAfterPublish === false ? "no" : "yes",
  };
}
